import type {
  Account,
  Assumptions,
  Compensation,
  ContributionRule,
  DbExport,
  EventItem,
  FundingScenario,
  LargePurchase,
  PurchaseEvent,
  Scenario,
  ScenarioMeta,
  Snapshot,
} from '../types';
import { buildSeed } from './seed';
import { buildDemoSeed } from './demoSeed';
import { buildOnboardingSeed, type OnboardingInput } from './onboarding';
import { uuid } from '../utils/uuid';
import { getStorage, type ProfileMeta } from './storage';

// ── Active profile ─────────────────────────────────────────────────────────
//
// The active profile is the one the web app reads from / writes to. It
// persists in localStorage so the UI stays on the same profile across page
// loads. Profile switching does NOT reload the page — the store calls
// `reload()` after switching and React re-renders only what changed.

const ACTIVE_PROFILE_KEY = 'cairn.activeProfile';
export const DEFAULT_PROFILE = 'main';
export const DEMO_PROFILE = 'demo';

let activeProfile = readSavedProfile();

function readSavedProfile(): string {
  try {
    return localStorage.getItem(ACTIVE_PROFILE_KEY) || DEFAULT_PROFILE;
  } catch {
    return DEFAULT_PROFILE;
  }
}

export function getActiveProfile(): string {
  return activeProfile;
}

/** Switch the active profile. Caller is responsible for re-fetching data
 *  (the store's reload()). No page reload. */
export function setActiveProfile(name: string): void {
  if (name === activeProfile) return;
  try {
    localStorage.setItem(ACTIVE_PROFILE_KEY, name);
  } catch {
    /* ignore — switch still applies for this session */
  }
  activeProfile = name;
  lastSeenMtime = 0; // re-arm polling against the new profile
}

// ── Profile management ────────────────────────────────────────────────────

export type { ProfileMeta };

export async function listProfiles(): Promise<ProfileMeta[]> {
  const storage = await getStorage();
  return storage.listProfiles();
}

/**
 * True iff at least one profile exists in the active storage backend. Used
 * by the boot flow to distinguish a first-time visitor (no profiles → show
 * the welcome screen) from a returning visitor (has profiles → jump
 * straight to the dashboard).
 *
 * Persisted in IndexedDB in prod, so a returning visitor is detected
 * reliably across page reloads without a separate flag.
 */
export async function hasAnyProfile(): Promise<boolean> {
  const storage = await getStorage();
  const list = await storage.listProfiles();
  return list.length > 0;
}

/**
 * Materialize the default (`main`) profile from a first-run onboarding form.
 * Writes directly to storage without going through the seed auto-materializer,
 * so the visitor's declared numbers land verbatim — no illustrative
 * placeholders leak in. Sets `main` active on completion.
 */
export async function completeOnboarding(input: OnboardingInput): Promise<void> {
  const storage = await getStorage();
  const seed = buildOnboardingSeed(input);
  const data: DbExport = {
    version: 2,
    exportedAt: new Date().toISOString(),
    ...seed,
  };
  await storage.writeProfile(DEFAULT_PROFILE, data);
  setActiveProfile(DEFAULT_PROFILE);
}

export async function createProfile(
  name: string,
  template: 'seed' | 'demo' | 'blank' = 'seed',
): Promise<void> {
  const storage = await getStorage();
  const data: DbExport = {
    version: 2,
    exportedAt: new Date().toISOString(),
    ...(template === 'demo'
      ? buildDemoSeed()
      : template === 'blank'
        ? buildBlankSeed()
        : buildSeed()),
  };
  await storage.writeProfile(name, data);
}

export async function deleteProfile(name: string): Promise<void> {
  const storage = await getStorage();
  await storage.deleteProfile(name);
}

export async function duplicateProfile(from: string, to: string): Promise<void> {
  const storage = await getStorage();
  await storage.duplicateProfile(from, to);
}

/** Wipe a profile and re-seed it from the demo dataset. */
export async function resetProfileToDemoSeed(name: string): Promise<void> {
  const storage = await getStorage();
  const data: DbExport = {
    version: 2,
    exportedAt: new Date().toISOString(),
    ...buildDemoSeed(),
  };
  await storage.writeProfile(name, data);
}

function buildBlankSeed() {
  const now = new Date().toISOString();
  const baseline: Scenario = {
    id: 'baseline',
    name: 'Baseline',
    description: 'Your current plan of record',
    icon: '🗻',
    color: '#3b54e8',
    order: 0,
    archived: false,
    events: [],
    createdAt: now,
  };
  const seed = buildSeed();
  return {
    accounts: [],
    snapshots: [],
    assumptions: seed.assumptions,
    purchases: [] as LargePurchase[],
    scenarios: [baseline],
    meta: {
      id: 'singleton',
      activeScenarioId: baseline.id,
      compareScenarioIds: [],
      updatedAt: now,
    } satisfies ScenarioMeta,
  };
}

// ── Active-profile read / mutate ──────────────────────────────────────────

let lastSeenMtime = 0;

async function readActive(): Promise<DbExport> {
  const storage = await getStorage();
  let data = await storage.readProfile(activeProfile);
  if (!data) {
    // First read: materialize a starter profile. Demo gets the demo seed;
    // anything else gets the placeholder starter seed.
    const seed = activeProfile === DEMO_PROFILE ? buildDemoSeed() : buildSeed();
    data = { version: 2, exportedAt: new Date().toISOString(), ...seed };
    await storage.writeProfile(activeProfile, data);
  }
  return data;
}

async function mutateActive(patch: (d: DbExport) => DbExport): Promise<void> {
  const storage = await getStorage();
  const current = await readActive();
  const next = patch(current);
  next.exportedAt = new Date().toISOString();
  await storage.writeProfile(activeProfile, next);
}

/** Polled by the store (in dev / file-backed mode). Returns the latest data
 *  if the active profile has been mutated by an external writer (e.g. CLI)
 *  since the last poll, or null if nothing changed. */
export async function pollActiveProfile(): Promise<DbExport | null> {
  const storage = await getStorage();
  if (!storage.polls) return null;
  try {
    const metas = await storage.pollMeta();
    const mine = metas.find(m => m.name === activeProfile);
    if (!mine) return null;
    if (mine.mtime !== lastSeenMtime) {
      lastSeenMtime = mine.mtime;
      return await storage.readProfile(activeProfile);
    }
  } catch {
    /* transient — try again next tick */
  }
  return null;
}

// ── Migrations ────────────────────────────────────────────────────────────
// Runs on read, not as versioned upgrades — the schema grew over time and
// lenient-read / strict-write keeps old exports interoperable.

export function migrateAssumptions(a: Assumptions): Assumptions {
  const defaults = {
    filingStatus: 'married' as const,
    ltcgRate: 0.15,
    ordinaryRate: 0.32,
    stateTaxRate: 0.05,
    earlyWithdrawalPenalty: 0.1,
    homeCostBasis: 300_000,
  };
  const derivedSalary =
    a.grossAnnualSalary ??
    Math.max(
      1,
      Math.round(
        (a.annualContribution401k + a.annualContributionTaxable + a.annualContributionHysa) /
          0.55 +
          (a.monthlyHouseholdSpend * 12) / 0.65,
      ),
    );
  const filingStatus = a.filingStatus ?? defaults.filingStatus;
  return {
    ...a,
    // Missing / empty displayName is legitimate — existing profiles have no
    // owner-facing name until the user edits it. Normalize to a trimmed
    // string so the UI's `displayName || 'Cairn'` fallback works cleanly.
    displayName: typeof a.displayName === 'string' ? a.displayName.trim() : '',
    filingStatus,
    householdSize: a.householdSize ?? (filingStatus === 'married' ? 2 : 1),
    ltcgRate: a.ltcgRate ?? defaults.ltcgRate,
    ordinaryRate: a.ordinaryRate ?? defaults.ordinaryRate,
    stateTaxRate: a.stateTaxRate ?? defaults.stateTaxRate,
    earlyWithdrawalPenalty: a.earlyWithdrawalPenalty ?? defaults.earlyWithdrawalPenalty,
    homeCostBasis: a.homeCostBasis ?? defaults.homeCostBasis,
    grossAnnualSalary: derivedSalary,
    compensation: a.compensation ?? deriveCompensation(derivedSalary),
    contributionRules: a.contributionRules ?? deriveContributionRules(a),
  };
}

/** Synthesize a `Compensation` from a legacy `grossAnnualSalary`. The
 *  legacy field doesn't separate base from bonus, so all of it lands in
 *  `baseAnnual`. The user can split it post-migration via the UI / CLI. */
function deriveCompensation(grossAnnualSalary: number): Compensation {
  return {
    baseAnnual: grossAnnualSalary,
    bonusAnnual: 0,
    bonusMonth: 2,
  };
}

/** Synthesize a `ContributionRule[]` from the three legacy
 *  `annualContribution{401k,Taxable,Hysa}` fields. Each becomes a
 *  fixed-dollar rule targeting the corresponding bucket. The 401(k) rule
 *  is treated as a January lump-sum (most common pattern for high earners
 *  who hit the cap fast); HYSA + taxable are monthly streams. */
function deriveContributionRules(a: Assumptions): ContributionRule[] {
  const rules: ContributionRule[] = [];
  const ts = a.updatedAt ?? new Date().toISOString();
  if (a.annualContribution401k > 0) {
    rules.push({
      id: 'rule-retirement-legacy',
      name: '401(k) (legacy)',
      target: { kind: 'bucket', bucket: 'retirement' },
      mode: { kind: 'fixed_dollar', annual: a.annualContribution401k },
      schedule: { kind: 'lump_sum', month: 1 },
      taxTreatment: 'pretax',
      enabled: true,
      order: rules.length,
      notes: `Migrated from legacy annualContribution401k=${a.annualContribution401k} on ${ts}`,
    });
  }
  if (a.annualContributionTaxable > 0) {
    rules.push({
      id: 'rule-taxable-legacy',
      name: 'Taxable brokerage (legacy)',
      target: { kind: 'bucket', bucket: 'taxable' },
      mode: { kind: 'fixed_dollar', annual: a.annualContributionTaxable },
      schedule: { kind: 'monthly' },
      taxTreatment: 'after_tax_brokerage',
      enabled: true,
      order: rules.length,
      notes: `Migrated from legacy annualContributionTaxable=${a.annualContributionTaxable} on ${ts}`,
    });
  }
  if (a.annualContributionHysa > 0) {
    rules.push({
      id: 'rule-hysa-legacy',
      name: 'HYSA (legacy)',
      target: { kind: 'bucket', bucket: 'hysa' },
      mode: { kind: 'fixed_dollar', annual: a.annualContributionHysa },
      schedule: { kind: 'monthly' },
      enabled: true,
      order: rules.length,
      notes: `Migrated from legacy annualContributionHysa=${a.annualContributionHysa} on ${ts}`,
    });
  }
  return rules;
}

function migratePurchase(p: LargePurchase): LargePurchase {
  if (p.scenarios && p.scenarios.length > 0 && p.activeScenarioId) {
    return p.enabled === undefined ? { ...p, enabled: true } : p;
  }
  const src = p.sourceAccountId ?? 'hysa';
  const sourceType = src.includes('taxable')
    ? ('taxable_ltcg' as const)
    : src.includes('retirement') || src.includes('401k')
      ? ('retirement_pretax' as const)
      : ('hysa' as const);
  const scenario: FundingScenario = {
    id: uuid(),
    name: 'Default',
    sources: [
      sourceType === 'hysa'
        ? { id: uuid(), type: 'hysa', amount: p.amount }
        : sourceType === 'taxable_ltcg'
          ? {
              id: uuid(),
              type: 'taxable_ltcg',
              grossAmount: p.amount,
              gainFraction: 0.5,
            }
          : {
              id: uuid(),
              type: 'retirement_pretax',
              grossAmount: p.amount,
            },
    ],
  };
  return {
    ...p,
    scenarios: [scenario],
    activeScenarioId: scenario.id,
    enabled: p.enabled ?? true,
  };
}

/** Convert a legacy LargePurchase into a v2 PurchaseEvent. */
export function purchaseToEvent(p: LargePurchase): PurchaseEvent {
  const migrated = migratePurchase(p);
  const active =
    migrated.scenarios?.find(s => s.id === migrated.activeScenarioId) ??
    migrated.scenarios?.[0];
  return {
    id: p.id,
    kind: 'purchase',
    yearOffset: p.yearOffset,
    name: p.name || 'Planned purchase',
    notes: p.notes,
    enabled: p.enabled !== false,
    newHomeValue: p.newHomeValue,
    amount: p.amount,
    funding: active,
    createdAt: p.createdAt,
  };
}

/** Build the initial Baseline scenario from a legacy purchases array.
 *  Used on import of v1 exports. */
export function buildBaselineScenario(purchases: LargePurchase[]): Scenario {
  return {
    id: 'baseline',
    name: 'Baseline',
    description: 'Your current plan of record',
    icon: '🗻',
    color: '#3b54e8',
    order: 0,
    archived: false,
    events: purchases.filter(p => !p.archived).map(purchaseToEvent),
    createdAt: new Date().toISOString(),
  };
}

function migrateScenario(s: Scenario): Scenario {
  return {
    ...s,
    icon: s.icon ?? '•',
    color: s.color ?? '#3b54e8',
    description: s.description,
    archived: s.archived ?? false,
    events: (s.events ?? []).map(migrateEvent),
  };
}

function migrateEvent(e: EventItem): EventItem {
  const base = { ...e, enabled: e.enabled ?? true } as EventItem;
  const legacy = e as EventItem & {
    annualDelta?: number;
    retirementDelta?: number;
    taxableDelta?: number;
    hysaDelta?: number;
  };
  if (
    e.kind === 'income_change' &&
    'annualDelta' in legacy &&
    legacy.annualDelta !== undefined &&
    (e as IncomeLike).newAnnualSalary === undefined
  ) {
    return {
      ...base,
      kind: 'income_change',
      newAnnualSalary: Math.max(0, legacy.annualDelta),
    } as EventItem;
  }
  if (
    e.kind === 'contribution_change' &&
    ('retirementDelta' in legacy || 'taxableDelta' in legacy || 'hysaDelta' in legacy) &&
    (e as ContribLike).newRetirement === undefined &&
    (e as ContribLike).newTaxable === undefined &&
    (e as ContribLike).newHysa === undefined
  ) {
    const clamp = (v: number | undefined): number | undefined =>
      v === undefined ? undefined : Math.max(0, v);
    return {
      ...base,
      kind: 'contribution_change',
      newRetirement:
        legacy.retirementDelta !== undefined && legacy.retirementDelta < 0
          ? 0
          : clamp(legacy.retirementDelta),
      newTaxable:
        legacy.taxableDelta !== undefined && legacy.taxableDelta < 0
          ? 0
          : clamp(legacy.taxableDelta),
      newHysa:
        legacy.hysaDelta !== undefined && legacy.hysaDelta < 0
          ? 0
          : clamp(legacy.hysaDelta),
    } as EventItem;
  }
  return base;
}

type IncomeLike = { newAnnualSalary?: number };
type ContribLike = { newRetirement?: number; newTaxable?: number; newHysa?: number };

// ── Accounts ──────────────────────────────────────────────────────────────

export async function listAccounts(): Promise<Account[]> {
  const d = await readActive();
  return [...(d.accounts ?? [])]
    .filter(a => !a.archived)
    .sort((a, b) => a.order - b.order);
}

export async function listAllAccounts(): Promise<Account[]> {
  const d = await readActive();
  return [...(d.accounts ?? [])].sort((a, b) => a.order - b.order);
}

export async function putAccount(a: Account): Promise<void> {
  await mutateActive(d => ({
    ...d,
    accounts: [...(d.accounts ?? []).filter(x => x.id !== a.id), a],
  }));
}

export async function deleteAccount(id: string): Promise<void> {
  await mutateActive(d => ({
    ...d,
    accounts: (d.accounts ?? []).filter(x => x.id !== id),
  }));
}

// ── Snapshots ─────────────────────────────────────────────────────────────

export async function listSnapshots(): Promise<Snapshot[]> {
  const d = await readActive();
  return [...(d.snapshots ?? [])].sort((a, b) => {
    const ka = `${a.date}T${a.createdAt}`;
    const kb = `${b.date}T${b.createdAt}`;
    return ka < kb ? 1 : ka > kb ? -1 : 0;
  });
}

export async function putSnapshot(s: Snapshot): Promise<void> {
  await mutateActive(d => ({
    ...d,
    snapshots: [...(d.snapshots ?? []).filter(x => x.id !== s.id), s],
  }));
}

export async function deleteSnapshot(id: string): Promise<void> {
  await mutateActive(d => ({
    ...d,
    snapshots: (d.snapshots ?? []).filter(x => x.id !== id),
  }));
}

// ── Assumptions ───────────────────────────────────────────────────────────

export async function getAssumptions(): Promise<Assumptions> {
  const d = await readActive();
  return migrateAssumptions(d.assumptions);
}

export async function putAssumptions(a: Assumptions): Promise<void> {
  const next = { ...a, updatedAt: new Date().toISOString() };
  await mutateActive(d => ({ ...d, assumptions: next }));
}

// ── Legacy purchases (kept for v1 export/import compat) ───────────────────

export async function listPurchases(): Promise<LargePurchase[]> {
  const d = await readActive();
  return [...(d.purchases ?? [])]
    .map(migratePurchase)
    .filter(p => !p.archived)
    .sort((a, b) => a.yearOffset - b.yearOffset);
}

export async function putPurchase(p: LargePurchase): Promise<void> {
  await mutateActive(d => ({
    ...d,
    purchases: [...(d.purchases ?? []).filter(x => x.id !== p.id), p],
  }));
}

export async function deletePurchase(id: string): Promise<void> {
  await mutateActive(d => ({
    ...d,
    purchases: (d.purchases ?? []).filter(x => x.id !== id),
  }));
}

// ── Scenarios ─────────────────────────────────────────────────────────────

export async function listScenarios(): Promise<Scenario[]> {
  const d = await readActive();
  return [...(d.scenarios ?? [])]
    .map(migrateScenario)
    .sort((a, b) => a.order - b.order);
}

export async function getScenario(id: string): Promise<Scenario | null> {
  const d = await readActive();
  const raw = (d.scenarios ?? []).find(s => s.id === id);
  return raw ? migrateScenario(raw) : null;
}

export async function putScenario(s: Scenario): Promise<void> {
  await mutateActive(d => ({
    ...d,
    scenarios: [...(d.scenarios ?? []).filter(x => x.id !== s.id), s],
  }));
}

export async function deleteScenario(id: string): Promise<void> {
  await mutateActive(d => ({
    ...d,
    scenarios: (d.scenarios ?? []).filter(x => x.id !== id),
  }));
}

// ── Meta ──────────────────────────────────────────────────────────────────

export async function getMeta(): Promise<ScenarioMeta> {
  const d = await readActive();
  if (d.meta) return d.meta;
  const initial: ScenarioMeta = {
    id: 'singleton',
    activeScenarioId: d.scenarios?.[0]?.id ?? 'baseline',
    compareScenarioIds: [],
    updatedAt: new Date().toISOString(),
  };
  await mutateActive(c => ({ ...c, meta: initial }));
  return initial;
}

export async function putMeta(m: ScenarioMeta): Promise<void> {
  const next = { ...m, updatedAt: new Date().toISOString() };
  await mutateActive(d => ({ ...d, meta: next }));
}

// ── Seed / wipe / export / import ─────────────────────────────────────────

export async function ensureSeeded(): Promise<void> {
  // readActive() materializes the seed if the profile doesn't exist yet.
  await readActive();
}

export async function exportAll(): Promise<DbExport> {
  const d = await readActive();
  return { ...d, version: 2, exportedAt: new Date().toISOString() };
}

export async function importAll(
  data: DbExport | (Omit<DbExport, 'version' | 'scenarios' | 'meta'> & { version: 1 }),
): Promise<void> {
  if (data.version !== 1 && data.version !== 2) {
    throw new Error(`Unsupported export version: ${(data as { version: number }).version}`);
  }
  const storage = await getStorage();
  let toWrite: DbExport;
  if (data.version === 1) {
    const baseline = buildBaselineScenario(
      (data.purchases ?? []).map(migratePurchase),
    );
    toWrite = {
      version: 2,
      exportedAt: new Date().toISOString(),
      accounts: data.accounts,
      snapshots: data.snapshots,
      assumptions: data.assumptions,
      purchases: data.purchases ?? [],
      scenarios: [baseline],
      meta: {
        id: 'singleton',
        activeScenarioId: baseline.id,
        compareScenarioIds: [],
        updatedAt: new Date().toISOString(),
      },
    };
  } else {
    toWrite = { ...data, exportedAt: new Date().toISOString() };
  }
  await storage.writeProfile(activeProfile, toWrite);
}

export async function wipe(): Promise<void> {
  const storage = await getStorage();
  const seed = buildSeed();
  await storage.writeProfile(activeProfile, {
    version: 2,
    exportedAt: new Date().toISOString(),
    ...seed,
  });
}
